import type {ReactElement} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {EmbeddedEditorAction, openDesignInEmbeddedEditor} from '@Libraries/embedded-editor';
import type {PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {getFilteredDesigns, getWizardUploadDesignsModalOpts} from '@Libraries/social-media-wizard-design-library';
import {MAX_UPLOADS_TO_DESIGNS} from '@Libraries/social-media-wizard-design-library.types';
import {getStartingURIForGallery} from '@Libraries/social-media-wizard-step-library';
import {getPostersPageIds} from '@Libraries/poster-library';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {openSocialMediaAddDesignModal} from '@Modals/social-media-add-design-modal/social-media-add-design-modal';
import {openDesignSelectorModal} from '@Modals/design-selector-modal/design-selector-modal';
import {
  disableNextButton,
  enableNextButton,
  updateDesignsToPublish,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {useAppDispatch} from '@/hooks';
import type {WizardDesignActionsProps} from './wizard-design-actions.types';
import styles from '../../social-media-wizard.module.scss';

export function WizardDesignActions({
  designsToPublishTo,
  onDesignsStaged,
  onEditDesign,
  onEmbeddedEditorOpened,
  isLoadingEmbeddedEditor,
  setIsPreviewLoading,
  setIsLoadingEmbeddedEditor,
}: WizardDesignActionsProps): ReactElement {
  const dispatch = useAppDispatch();

  const getAddDesignUploadModalOpts = (): CreateDesignFromMediaModalOpts => {
    return getWizardUploadDesignsModalOpts({
      onDesignsCreated: (uploadDesigns: DesignSelection[]): void => {
        dispatch(updateDesignsToPublish([...designsToPublishTo, ...getFilteredDesigns(uploadDesigns, MAX_UPLOADS_TO_DESIGNS)]));
        dispatch(enableNextButton());
      },
      onPosterCreationsStarted: (): void => {
        setIsPreviewLoading(true);
        dispatch(disableNextButton());
      },
    });
  };

  const addDesignModalMyUploadsSelectionCallback = (): void => {
    window.PMW.triggerCreateDesignFromUserUpload({
      ...getAddDesignUploadModalOpts(),
      footerPrimaryCtaText: window.i18next.t('pmwjs_add_selected'),
      hideFooterSecondaryActions: true,
    });
  };

  const addDesignModalMyDesignsSelectionCallback = (): void => {
    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.MY_DESIGNS, DesignSelectorTab.SHARED_WITH_ME],
      onAddSelected: (selectedDesigns: DesignSelection[]) => {
        setIsPreviewLoading(true);
        onDesignsStaged(selectedDesigns);
        dispatch(updateDesignsToPublish(selectedDesigns));
      },
      selectedDesigns: designsToPublishTo,
    });
  };

  const onAddDesignGalleryTemplateSelected = (templateSelected: DesignSelection): void => {
    setIsPreviewLoading(true);
    setIsLoadingEmbeddedEditor(true);

    openDesignInEmbeddedEditor(
      templateSelected.posterHashedId,
      onEmbeddedEditorOpened,
      (newDesignId: string) => {
        getPostersPageIds([newDesignId])
          .then((pageIdsMap: Record<PosterHashedId, PageHashedId[]>) => {
            if (!pageIdsMap[newDesignId]) {
              console.error('No page IDs found for poster:', newDesignId);
              return;
            }

            const newDesignSelections = pageIdsMap[newDesignId].map((pageId): DesignSelection => {
              return {posterHashedId: newDesignId, pageHashedId: pageId};
            });

            dispatch(updateDesignsToPublish([...designsToPublishTo, ...newDesignSelections]));
          })
          .catch((err: unknown) => {
            console.error('Error fetching poster page IDs:', err);
          });
      },
      () => {
        setIsPreviewLoading(false);
      },
      EmbeddedEditorAction.COPY
    );
  };

  const addDesignModalCreateDesignSelectionCallback = (): void => {
    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.GALLERY_TEMPLATES],
      selectedDesigns: designsToPublishTo,
      designGalleryStartingURI: getStartingURIForGallery(),
      onDesignSelectionCallback: onAddDesignGalleryTemplateSelected,
      startDesignFromUploadBtnOpts: getAddDesignUploadModalOpts(),
      designGalleryHeight: '88vh',
    });
  };

  const onAddDesignClick = (): void => {
    openSocialMediaAddDesignModal({
      myUploadsSelectionCallback: addDesignModalMyUploadsSelectionCallback,
      myDesignsSelectionCallback: addDesignModalMyDesignsSelectionCallback,
      createDesignSelectionCallback: addDesignModalCreateDesignSelectionCallback,
    });
  };

  const getAddDesignCta = (): ReactElement => {
    return (
      <Button
        type={Type.SECONDARY}
        icon="icon icon-plus"
        size={Size.SMALL}
        customClasses={`${styles.buttonShadow} ${styles.editDesignButton} spacing-m-r-2`}
        text={window.i18next.t('pmwjs_add_design')}
        iconPlacement={IconPlacement.LEFT}
        onClick={onAddDesignClick}
      />
    );
  };

  const getEditDesignCta = (): ReactElement => {
    return (
      <Button
        type={Type.SECONDARY}
        icon="icon icon-pencil"
        size={Size.SMALL}
        customClasses={`${styles.buttonShadow} ${styles.editDesignButton}`}
        text={window.i18next.t('pmwjs_edit_design_capitalized')}
        iconPlacement={IconPlacement.LEFT}
        onClick={onEditDesign}
        isLoading={isLoadingEmbeddedEditor}
      />
    );
  };

  return (
    <div className="flex-h-row">
      {getAddDesignCta()}
      {getEditDesignCta()}
    </div>
  );
}
